import {
  BASKET_PROMO_SLAB_LEVEL,
  CHANNEL_CUSTOMER_APP,
  OPERATORS,
  PROMO_FIELDS,
  PROMO_SLAB_LEVEL,
  PROMO_TYPE,
  PROMO_VALUE_TYPE,
  STATUS,
} from "../config/constants";
import {
  IAddUpdateBasketPromoState,
  IAddUpdateDynamicPromoState,
  IAddUpdateUserLevelPromoState,
  IAddUpdateVolumetricPromoState,
  IAll,
  IAny,
  IBasketMeta,
  IBasketPromoDetailResponseNormalized,
  ICondition,
  ICreateBasketPromoPayload,
  ICreatePromoPayload,
  IDynamicPromoDetailResponse,
  IDynamicPromoMeta,
  IExcludedProduct,
  IFetchPromoResponse,
  IMeta,
  INormalizedPromoIncludedCategories,
  IOrderAddUpdateList,
  IPromoBasketDetailResponse,
  IPromoDetailResponse,
  IPromoDiscountSlabs,
  IPromoIncludedCategories,
  IPromoMeta,
  IVolumetricPromoSlabs,
} from "../interfaces/Index";
import { formatDate } from "../utils/lib";

export const normalizeFetchPromoResponse = (
  res: Array<IFetchPromoResponse>,
  pagination: number | boolean,
  isPaginatedRes: boolean
) => {
  let payload: any = {
    pagination: res.length === 50 ? pagination : false,
    isPaginatedRes,
  };
  payload.promosData = res.map((promo: IFetchPromoResponse) => {
    return {
      id: promo.promoId ? promo.promoId : "",
      promoCode: promo.promoCode ? promo.promoCode : "",
      promoDescription: promo.promoDescription ? promo.promoDescription : "",
      promoStatus: promo.status,
      variantId: promo.payload.variantId,
      sku: promo.payload.sku,
      productTitle: promo.payload.title,
      productId: promo.payload.productId,
    };
  });
  return payload;
};

export const normalizeIncludedCategories = (
  includedCategories: IPromoIncludedCategories[]
): INormalizedPromoIncludedCategories[] => {
  return includedCategories.map((cat) => {
    return {
      ...cat,
      id: cat.categoryId,
      title: cat.name,
      key: cat.categoryId,
      value: cat.name,
    };
  });
};

export const normalizeExcludedProducts = (
  excludedProducts: IExcludedProduct[]
) => {
  return excludedProducts.map((product) => {
    return {
      id: product.productId,
      variantId: Number(product.variantId),
      itemName: product.title,
      itemPrice: 0,
      quantity: 0,
      discount: 0,
      sku: product.sku,
    };
  });
};

export const normalizeAndMapSlabs = (discountSlab: IPromoDiscountSlabs[]) => {
  return discountSlab.map((slab) => {
    return {
      minimumUnits: Number(slab.quantity),
      discount: Number(slab.discount),
    };
  });
};

export const normalizeBasketPromoDetailsResponse = (
  res: IPromoBasketDetailResponse
): IBasketPromoDetailResponseNormalized => {
  return {
    id: res.promoId,
    channel: res.channels && res.channels.length > 0 ? res.channels[0] : "",
    customerChannels: normalizeCustomerChannels(res.customerChannels),
    promoCode: res.promoCode,
    promoStatus: res.status,
    promoDescription: res.promoDescription,
    type: res.type || "",
    createdBy: res.createdBy || "",
    updatedBy: res.updatedBy || "",
    createdAt: formatDate(res.createdAt, "do MMM, h:mm aaa"),
    updatedAt: formatDate(res.updatedAt, "do MMM, h:mm aaa"),
    minOrderValue: res.discountSlab ? Number(res.discountSlab[0].amount) : 0,
    discountValue: res.discountSlab ? Number(res.discountSlab[0].discount) : 0,
    basketProducts: normalizeExcludedProducts(res.payload.excludedProducts),
    basketCategories: res.payload.includedCategories || [],
  };
};

export const normalizePromoDetailsResponse = (res: IPromoDetailResponse) => {
  return {
    id: res.promoId,
    promoCode: res.promoCode,
    promoStatus: res.status,
    promoDescription: res.promoDescription,
    type: res.type || "",
    createdBy: res.createdBy || "",
    updatedBy: res.updatedBy || "",
    createdAt: formatDate(res.createdAt, "do MMM, h:mm aaa").toUpperCase(),
    updatedAt: formatDate(res.updatedAt, "do MMM, h:mm aaa").toUpperCase(),
    slabs: normalizeAndMapSlabs((res.discountSlab as any) || []),
    channel: res.channels && res.channels.length > 0 ? res.channels[0] : "",
    customerChannels: normalizeCustomerChannels(res.customerChannels),
    promoMeta: {
      sku: (res.payload as IPromoMeta).sku,
      variantId: (res.payload as IPromoMeta).variantId,
      productTitle: (res.payload as IPromoMeta).title,
      status: (res.payload as IPromoMeta).status,
      type: (res.payload as IPromoMeta).type,
    },
  };
};

export const normalizeDynamicPromoDetailsResponse = (
  res: IDynamicPromoDetailResponse
) => {
  return {
    id: res.promoId,
    promoCode: res.promoCode,
    promoStatus: res.status,
    promoDescription: res.promoDescription,
    type: res.type || "",
    createdBy: res.createdBy || "",
    updatedBy: res.updatedBy || "",
    createdAt: formatDate(res.createdAt, "do MMM, h:mm aaa").toUpperCase(),
    updatedAt: formatDate(res.updatedAt, "do MMM, h:mm aaa").toUpperCase(),
    // discount: res.discount as any || [],
    discountPerUnit: Object.assign({}, ...res.discountSlab),
    channel: res.channels && res.channels.length > 0 ? res.channels[0] : "",
    customerChannels: normalizeCustomerChannels(res.customerChannels),
    promoMeta: {
      sku: (res.payload as IDynamicPromoMeta).sku,
      variantId: (res.payload as IDynamicPromoMeta).variantId,
      productTitle: (res.payload as IDynamicPromoMeta).title,
      status: (res.payload as IDynamicPromoMeta).status,
      type: (res.payload as IDynamicPromoMeta).type,
      includedUcs: (res.payload as IDynamicPromoMeta).includedUcs,
      includedWarehouses: (res.payload as IDynamicPromoMeta).includedWarehouses,
    },
  };
};

export const normalizedVolumetricCreatePromoPayload = (
  payload: IAddUpdateVolumetricPromoState
): ICreatePromoPayload => {
  return {
    promoType: PROMO_TYPE.VOLUMETRIC,
    promoChannels: [`${payload.channel}`],
    promoDescription: payload.promoDescription,
    customerChannels: payload.customerChannels.map(({ id }) => id),
    requestBody: {
      productId: payload.productId,
      sku: payload.productSku,
      status: payload.promoStatus ? "ACTIVE" : "INACTIVE",
      title: payload.productTitle,
      type: PROMO_TYPE.VOLUMETRIC,
      variantId: payload.productVariantId as string,
    },
    meta: generateVolumetricPromoCreateMeta(payload),
    //will not be used
    root: {
      conditions: generateVolumetricPromoConditions(payload),
    },
  };
};

export const normalizedDynamicCreatePromoPayload = (
  payload: IAddUpdateDynamicPromoState,
  ucList: any,
  warehouseList: any
): ICreatePromoPayload | any => {
  if (ucList.length >= 0 && warehouseList.length == 0) {
    return {
      promoType: PROMO_TYPE.DYNAMIC,
      promoChannels: [CHANNEL_CUSTOMER_APP],
      customerChannels: payload.customerChannels.map(({ id }) => id),
      promoDescription: payload.promoDescription,
      requestBody: {
        sku: payload.productSku,
        type: PROMO_TYPE.DYNAMIC,
        title: payload.productTitle,
        status: payload.promoStatus ? STATUS.ACTIVE : STATUS.INACTIVE,
        productId: payload.productId,
        variantId: payload.productVariantId as string,
        includedUcs: ucList,
      },
      meta: generateDynamicPromoCreateMeta(payload),
      root: {
        conditions: generateDynamicPromoConditions(payload),
      },
    };
  } else if (warehouseList.length >= 0 && ucList.length == 0) {
    return {
      promoType: PROMO_TYPE.DYNAMIC,
      promoChannels: ["CUSTOMER_APP"],
      promoDescription: payload.promoDescription,
      customerChannels: payload.customerChannels.map(({ id }) => id),
      requestBody: {
        sku: payload.productSku,
        type: PROMO_TYPE.DYNAMIC,
        title: payload.productTitle,
        status: payload.promoStatus ? "ACTIVE" : "INACTIVE",
        productId: payload.productId,
        variantId: payload.productVariantId as string,
        includedWarehouses: warehouseList,
      },
      meta: generateDynamicPromoCreateMeta(payload),
      root: {
        conditions: generateDynamicPromoConditions(payload),
      },
    };
  }
};

export const normalizedUserLevelCreatePromoPayload = (
  payload: IAddUpdateUserLevelPromoState
): ICreatePromoPayload | any => {
  return {
    promoType: PROMO_TYPE.ULP,
    promoChannels: [CHANNEL_CUSTOMER_APP],
    promoDescription: payload.promoDescription,
    customerChannels: payload.customerChannels.map(({ id }) => id),
    requestBody: {
      sku: payload.productSku,
      type: PROMO_TYPE.ULP,
      title: payload.productTitle,
      status: payload.promoStatus ? STATUS.ACTIVE : STATUS.INACTIVE,
      productId: payload.productId,
      variantId: payload.productVariantId as string,
    },
    meta: generateDynamicPromoCreateMeta(payload),
    root: {
      conditions: generateDynamicPromoConditions(payload),
    },
  };
};

export const normalizeCreateBasketPromoPayload = (
  payload: IAddUpdateBasketPromoState
): ICreateBasketPromoPayload => {
  return {
    promoType: PROMO_TYPE.BASKET,
    promoChannels: [`${payload.channel}`],
    promoDescription: payload.promoDescription,
    customerChannels: payload.customerChannels.map(({ id }) => id),
    requestBody: {
      status: payload.promoStatus ? "ACTIVE" : "INACTIVE",
      type: PROMO_TYPE.BASKET,
      excludedProducts: mapExcludedProducts(payload.basketProducts),
      includedCategories: [
        payload.basketCategories as INormalizedPromoIncludedCategories,
      ],
    },
    meta: generateBasketPromoCreateMeta(payload),
    root: {
      conditions: generateBasketPromoConditions(payload),
    },
  };
};

export const mapExcludedProducts = (
  products: IOrderAddUpdateList[]
): IExcludedProduct[] => {
  return products.map((product) => {
    return {
      productId: product.id.toString(),
      sku: product.sku as string,
      title: product.itemName,
      variantId: product.variantId.toString(),
    };
  });
};

export const generateVolumetricPromoCreateMeta = (
  payload: IAddUpdateVolumetricPromoState
): IMeta[] => {
  let meta: IMeta[] = [];
  for (let i = 0; i < PROMO_SLAB_LEVEL; i++) {
    if (
      payload.slabs[i] &&
      payload.slabs[i].minUnits &&
      payload.slabs[i].discountPerUnit
    ) {
      meta.push({
        quantity: payload.slabs[i].minUnits.toString(),
        discount: payload.slabs[i].discountPerUnit.toString(),
      });
    }
  }
  return meta;
};

export const generateDynamicPromoCreateMeta = (
  payload: IAddUpdateDynamicPromoState | IAddUpdateUserLevelPromoState
): IMeta[] => {
  let meta: IMeta[] = [];
  if (payload.discountPerUnit) {
    meta.push({
      quantity: "1",
      discount: payload.discountPerUnit?.toString(),
    });
  }
  return meta;
};

export const generateDynamicPromoUpdateMeta = (
  payload: IAddUpdateDynamicPromoState | IAddUpdateUserLevelPromoState
): IMeta[] => {
  let meta: IMeta[] = [];
  if (payload.discountPerUnit) {
    meta.push({
      quantity: "1",
      discount: payload.discountPerUnit?.toString(),
      amount: "",
    });
  }
  return meta;
};

export const generateBasketPromoCreateMeta = (
  payload: IAddUpdateBasketPromoState
): IBasketMeta[] => {
  let meta: IBasketMeta[] = [];
  for (let i = 0; i < BASKET_PROMO_SLAB_LEVEL; i++) {
    meta.push({
      amount: payload.minOrderValue.toString(),
      discount: payload.discountValue.toString(),
    });
  }
  return meta;
};

export const generateVolumetricPromoConditions = (
  payload: IAddUpdateVolumetricPromoState
): ICondition[] => {
  let condition: ICondition[] = [];
  for (let i = 0; i < PROMO_SLAB_LEVEL; i++) {
    if (
      payload.slabs[i] &&
      payload.slabs[i].minUnits &&
      payload.slabs[i].discountPerUnit
    ) {
      condition.push({
        event: {
          field: PROMO_FIELDS.DISCOUNT,
          value: Number(payload.slabs[i].discountPerUnit),
          valueType: PROMO_VALUE_TYPE.INTEGER,
        },
        any: generatePromoAny(payload.slabs[i]),
      });
    }
  }
  return condition;
};

export const generateDynamicPromoConditions = (
  payload: IAddUpdateDynamicPromoState | IAddUpdateUserLevelPromoState
): ICondition[] => {
  let condition: ICondition[] = [];

  if (payload.discountPerUnit) {
    condition.push({
      event: {
        field: PROMO_FIELDS.DISCOUNT,
        value: Number(payload.discountPerUnit),
        valueType: PROMO_VALUE_TYPE.FLOAT,
      },
      any: generatePromoAny(payload),
    });
  }
  return condition;
};

// export const generatePromoAnyDynamic = (payload: any): IAny[] => {
//   let any: IAny[] = [];
//   any.push({
//     all: generatePromoAll(payload),
//   });
//   return any;
// };

export const generatePromoAny = (
  payload: IVolumetricPromoSlabs | any
): IAny[] => {
  let any: IAny[] = [];
  any.push({
    all: generatePromoAll(payload),
  });
  return any;
};

// export const generatePromoAllDynamic = (payload: any): IAll[] => {
//   let all: IAll[] = [];
//   all.push({
//     field: PROMO_FIELDS.AMOUNT,
//     operator: OPERATORS.GREATER_THAN_EQUALS_TO,
//     value: payload.minUnits.toString(),
//     valueType: PROMO_VALUE_TYPE.FLOAT,
//   });
//   return all;
// };

export const generatePromoAll = (
  payload: IVolumetricPromoSlabs | any
): IAll[] => {
  let all: IAll[] = [];
  all.push({
    field: PROMO_FIELDS.QUANTITY,
    operator: OPERATORS.GREATER_THAN_EQUALS_TO,
    value: payload.minUnits?.toString(),
    valueType: PROMO_VALUE_TYPE.INTEGER,
  });
  return all;
};

export const generateBasketPromoAny = (
  payload: IAddUpdateBasketPromoState
): IAny[] => {
  let any: IAny[] = [];
  any.push({
    all: generateBasketPromoAll(payload),
  });
  return any;
};

export const generateBasketPromoAll = (
  payload: IAddUpdateBasketPromoState
): IAll[] => {
  let all: IAll[] = [];
  all.push({
    field: PROMO_FIELDS.AMOUNT,
    operator: OPERATORS.GREATER_THAN_EQUALS_TO,
    value: payload.minOrderValue.toString(),
    valueType: PROMO_VALUE_TYPE.FLOAT,
  });
  return all;
};

export const generateBasketPromoConditions = (
  payload: IAddUpdateBasketPromoState
): ICondition[] => {
  let condition: ICondition[] = [];
  for (let i = 0; i < BASKET_PROMO_SLAB_LEVEL; i++) {
    condition.push({
      event: {
        field: PROMO_FIELDS.DISCOUNT,
        value: Number(payload.discountValue),
        valueType: PROMO_VALUE_TYPE.FLOAT,
      },
      any: generateBasketPromoAny(payload),
    });
  }
  return condition;
};

export const normalizeCustomerChannels = (customerChannels: any) => {
  return customerChannels.map((customerChannel: any) => {
    return {
      id: customerChannel,
      title: customerChannel,
    };
  });
};
